/**
 * Roles Management Page by ali
 * Displays all available roles (default + custom).
 * Custom roles are stored in the admins team preferences.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { ADMINS_TEAM_ID, getTeamPrefs, updateTeamPrefs } from '../../appwrite';
import './Roles.css';

// Default roles — always available, cannot be deleted
const DEFAULT_ROLES = ['admin', 'manager', 'founder', 'salesOnly', 'production'];

const Roles = () => {
  const [customRoles, setCustomRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [newRoleName, setNewRoleName] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [deletingRole, setDeletingRole] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  /**
   * Fetches custom roles from admins team preferences.
   */
  const fetchCustomRoles = useCallback(async () => {
    setLoading(true);
    try {
      const prefs = await getTeamPrefs(ADMINS_TEAM_ID);
      if (prefs.customRoles && Array.isArray(prefs.customRoles)) {
        setCustomRoles(prefs.customRoles);
      }
    } catch (err) {
      console.warn('No custom roles found:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCustomRoles();
  }, [fetchCustomRoles]);

  /**
   * Creates a new custom role.
   */
  const handleCreateRole = async (e) => {
    e.preventDefault();
    const roleName = newRoleName.trim();
    if (!roleName) return;

    // Validate: no duplicates
    if (DEFAULT_ROLES.includes(roleName) || customRoles.includes(roleName)) {
      setError(`Role "${roleName}" already exists.`);
      return;
    }

    // Validate: alphanumeric only
    if (!/^[a-zA-Z][a-zA-Z0-9]*$/.test(roleName)) {
      setError('Role name must start with a letter and contain only letters and numbers.');
      return;
    }

    setIsSaving(true);
    setError('');

    try {
      const updatedRoles = [...customRoles, roleName];
      await updateTeamPrefs(ADMINS_TEAM_ID, { customRoles: updatedRoles });
      setCustomRoles(updatedRoles);
      setNewRoleName('');
      showSuccess(`Role "${roleName}" created successfully.`);
    } catch (err) {
      setError(err.message || 'Failed to create role.');
    } finally {
      setIsSaving(false);
    }
  };

  /**
   * Deletes a custom role from preferences.
   * Does NOT remove the label from users who already have it.
   */
  const handleDeleteRole = async (roleName) => {
    setDeletingRole(roleName);
    setError('');

    try {
      const updatedRoles = customRoles.filter(r => r !== roleName);
      await updateTeamPrefs(ADMINS_TEAM_ID, { customRoles: updatedRoles });
      setCustomRoles(updatedRoles);
      showSuccess(`Role "${roleName}" deleted.`);
    } catch (err) {
      setError(err.message || 'Failed to delete role.');
    } finally {
      setDeletingRole(null);
    }
  };

  const showSuccess = (message) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  // Loading state
  if (loading) {
    return (
      <div className="roles-page">
        <div className="roles-loading">
          <div className="loading-spinner"></div>
          <p>Loading roles...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="roles-page">
      {/* Page header */}
      <div className="page-header">
        <div className="page-header-left">
          <h1 className="page-title">Roles</h1>
          <span className="page-count">{DEFAULT_ROLES.length + customRoles.length} total</span>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="members-alert members-alert-error">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
            <path d="M8 1a7 7 0 100 14A7 7 0 008 1zm0 10.5a.75.75 0 110-1.5.75.75 0 010 1.5zM8.75 4.75v4a.75.75 0 01-1.5 0v-4a.75.75 0 011.5 0z" />
          </svg>
          <span>{error}</span>
          <button onClick={() => setError('')}>✕</button>
        </div>
      )}

      {/* Success message */}
      {successMessage && (
        <div className="members-alert members-alert-success">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
            <path d="M8 1a7 7 0 100 14A7 7 0 008 1zm3.22 5.47l-4 4a.75.75 0 01-1.06 0l-2-2a.75.75 0 011.06-1.06L6.69 8.88l3.47-3.47a.75.75 0 011.06 1.06z" />
          </svg>
          <span>{successMessage}</span>
        </div>
      )}

      {/* Create new role form */}
      <div className="roles-create-card">
        <h2 className="roles-section-title">Create New Role</h2>
        <form className="roles-create-form" onSubmit={handleCreateRole}>
          <div className="roles-input-wrapper">
            <input
              type="text"
              placeholder="Enter role name (e.g. reviewer)"
              value={newRoleName}
              onChange={(e) => setNewRoleName(e.target.value)}
              disabled={isSaving}
            />
          </div>
          <button type="submit" className="roles-create-btn" disabled={isSaving || !newRoleName.trim()}>
            {isSaving ? (
              <span className="btn-spinner"></span>
            ) : (
              <>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M8 3V13M3 8H13" />
                </svg>
                Add Role
              </>
            )}
          </button>
        </form>
      </div>

      {/* Roles lists */}
      <div className="roles-sections">
        {/* Default roles */}
        <div className="roles-section">
          <h2 className="roles-section-title">
            Default Roles
            <span className="roles-section-badge">{DEFAULT_ROLES.length}</span>
          </h2>
          <div className="roles-list">
            {DEFAULT_ROLES.map(role => (
              <div key={role} className="role-item role-item-default">
                <div className="role-item-info">
                  <span className="role-item-icon">
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.5">
                      <path d="M9 2L11 6.5H16L12 9.5L13.5 14.5L9 11.5L4.5 14.5L6 9.5L2 6.5H7L9 2Z" />
                    </svg>
                  </span>
                  <span className="role-item-name">{role}</span>
                </div>
                <span className="role-item-tag">default</span>
              </div>
            ))}
          </div>
        </div>

        {/* Custom roles */}
        <div className="roles-section">
          <h2 className="roles-section-title">
            Custom Roles
            <span className="roles-section-badge">{customRoles.length}</span>
          </h2>
          {customRoles.length > 0 ? (
            <div className="roles-list">
              {customRoles.map(role => (
                <div key={role} className="role-item role-item-custom">
                  <div className="role-item-info">
                    <span className="role-item-icon custom">
                      <svg width="18" height="18" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path d="M9 2L11 6.5H16L12 9.5L13.5 14.5L9 11.5L4.5 14.5L6 9.5L2 6.5H7L9 2Z" />
                      </svg>
                    </span>
                    <span className="role-item-name">{role}</span>
                  </div>
                  <button
                    className="role-delete-btn"
                    onClick={() => handleDeleteRole(role)}
                    disabled={deletingRole === role}
                    title={`Delete "${role}" role`}
                  >
                    {deletingRole === role ? (
                      <span className="btn-spinner small"></span>
                    ) : (
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
                        <path d="M3 4H13M5.5 4V3C5.5 2.45 5.95 2 6.5 2H9.5C10.05 2 10.5 2.45 10.5 3V4M6.5 7V11.5M9.5 7V11.5M4 4L4.75 13C4.75 13.55 5.2 14 5.75 14H10.25C10.8 14 11.25 13.55 11.25 13L12 4" />
                      </svg>
                    )}
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="roles-empty-custom">
              <p>No custom roles yet. Use the form above to create one.</p>
            </div>
          )}
        </div>
      </div>

      {/* Info note */}
      <div className="roles-info-note">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
          <path d="M8 1a7 7 0 100 14A7 7 0 008 1zm-.75 4.75a.75.75 0 011.5 0v4a.75.75 0 01-1.5 0v-4zM8 10.5a.75.75 0 110 1.5.75.75 0 010-1.5z" />
        </svg>
        <span>
          The <strong>owner</strong> role is special and can be assigned from the Members page.
          Roles are stored as <strong>user labels</strong> on each user's Appwrite account.
          Deleting a custom role here will <strong>not</strong> remove it from users who already have it.
        </span>
      </div>
    </div>
  );
};

export default Roles;
